<script>
import api from '@/api'
import { alertSuccess, alertConfirm, alertError } from '@/helpers/swal'
import pagination from '@/components/pagination.vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  props: {
    status: {
      type: String,
      required: false,
    },
    category: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      loading: true,
      pagetitle: 'Devices',
      devices: [],
      totalItems: 0,
      totalPages: 1,
      currentPage: 0,
      perPage: 50,
      baseUrl: `${window.apiUrl}/devices/getDevices`,
      exportBtnDisabled: false,
      statuses: ['critical', 'trouble', 'attention', 'clear', 'maintenance', 'offline'],
      categories: [],
      deviceTypes: [],
      searchHost: '',
      filters: {
        status: null,
        category: null,
        deviceType: null,
      },
      syncProgressMessage: '',
      sortBy: 'id',
      sortDir: 'asc',
      baseColumns: [
        { name: 'host', label: 'Host', sortable: true },
        { name: 'ipAddress', label: 'IP Address', sortable: true },
        { name: 'deviceType', label: 'Type', sortable: false },
        { name: 'category', label: 'Category', sortable: false },
        { name: 'status', label: 'Status', sortable: false },
        { name: 'availability', label: 'Availability', sortable: false },
      ],
      ezemanageColumns: [
        { name: 'description2', label: 'Remarks', sortable: false },
        { name: 'description3', label: 'Device Details', sortable: false },
      ],
      selectedDeviceIds: [],
      syncBtnDisabled: false,
    }
  },
  computed: {
    showEzemanageSyncActions() {
      return this.authUserIsEzemanage && this.authUserHasPermission('devices_edit')
    },
    allOnPageSelected() {
      return this.devices.length > 0 && this.devices.every((device) => this.selectedDeviceIds.includes(device.id))
    },
    tableColumns() {
      if (!this.authUserIsEzemanage) {
        return this.baseColumns
      }
      const cols = [...this.baseColumns]
      cols.splice(4, 0, ...this.ezemanageColumns)
      return cols
    },
    tableColCount() {
      let count = this.tableColumns.length + (this.showActionColumn() ? 1 : 0)
      if (this.showEzemanageSyncActions) {
        count += 1
      }
      return count
    },
  },
  components: {
    pagination,
    vSelect,
  },
  async mounted() {
    await this.getCategories()
    await this.getDeviceTypes()

    this.filters = {
      status: this.status || null,
      category: this.category || null,
      deviceType: this.deviceType || null,
    }

    // this.getDevices()
  },
  watch: {
    filters: {
      handler() {
        this.applyFilters()
      },
      deep: true,
    },
  },
  methods: {
    buildListFilterParams() {
      const params = {}
      if (this.filters.status) params.status = this.filters.status
      if (this.filters.category) params.category = this.filters.category
      if (this.filters.deviceType) params.deviceType = this.filters.deviceType
      if (this.searchHost) params.host = this.searchHost
      return params
    },

    getDevices() {
      this.loading = true
      const params = {
        ...this.buildListFilterParams(),
        page: this.currentPage,
        size: this.perPage,
        sortBy: this.sortBy,
        sortDir: this.sortDir,
      }
      api
        .get(this.baseUrl, { params })
        .then((response) => {
          this.devices = response.data?.devices || []
          this.totalItems = response.data.totalItems
          this.totalPages = response.data.totalPages
          this.currentPage = response.data.currentPage
          this.selectedDeviceIds = this.selectedDeviceIds.filter((id) =>
            this.devices.some((device) => device.id === id)
          )
        })
        .finally(() => {
          this.loading = false
        })
    },

    deleteDevice(id) {
      alertConfirm('You want to delete this device').then((result) => {
        if (result.value) {
          api.post(`/devices/delete/${id}`).then(() => {
            alertSuccess('Device deleted successfully')
            this.getDevices()
          })
        }
      })
    },

    async getCategories() {
      await api
        .get(`${window.apiUrl}/category/getCategories`)
        .then((response) => {
          this.categories = response.data || []
        })
        .finally(() => {
          this.loading = false
        })
    },

    async getDeviceTypes() {
      this.loading = true
      const params = {
        page: 0,
        size: 100,
      }
      await api
        .get(`${window.apiUrl}/device-types/list`, { params })
        .then((response) => {
          this.deviceTypes = response.data?.deviceTypes || []
        })
        .finally(() => {
          this.loading = false
        })
    },

    exportData() {
      if (!this.devices.length) {
        alertError('There is no data to export')
        return
      }
      this.exportBtnDisabled = true
      api
        .get('/devices/export')
        .then((response) => {
          let csvString = response.data
          this.exportCSVStringToFile('Devices.csv', csvString)
        })
        .finally(() => {
          this.exportBtnDisabled = false
        })
    },

    exportCSVStringToFile(filename, csvString) {
      const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' }) // Add BOM for Excel compatibility
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    applyFilters() {
      this.currentPage = 0
      this.getDevices()
    },

    sortByColumn(column) {
      if (column.sortable) {
        this.sortBy = column.name
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        this.currentPage = 0
        this.getDevices()
      }
    },

    updatePage(newPage) {
      this.currentPage = newPage - 1
      this.getDevices()
    },

    showActionColumn() {
      return this.authUserHasPermission('devices_edit') || this.authUserHasPermission('devices_delete')
    },

    handleStatusClick(host) {
      this.$router.push({ name: 'pending-alerts', query: { host: host } })
    },

    isDeviceSelected(deviceId) {
      return this.selectedDeviceIds.includes(deviceId)
    },

    toggleDeviceSelection(deviceId) {
      if (this.isDeviceSelected(deviceId)) {
        this.selectedDeviceIds = this.selectedDeviceIds.filter((id) => id !== deviceId)
      } else {
        this.selectedDeviceIds = [...this.selectedDeviceIds, deviceId]
      }
    },

    toggleSelectAllOnPage() {
      if (this.allOnPageSelected) {
        const pageIds = this.devices.map((device) => device.id)
        this.selectedDeviceIds = this.selectedDeviceIds.filter((id) => !pageIds.includes(id))
      } else {
        const merged = new Set([...this.selectedDeviceIds, ...this.devices.map((device) => device.id)])
        this.selectedDeviceIds = Array.from(merged)
      }
    },

    shortenMessage(message, maxLength = 120) {
      if (!message) return ''
      const text = String(message).replace(/\s+/g, ' ').trim()
      if (text.length <= maxLength) return text
      return text.slice(0, maxLength - 1) + '…'
    },

    formatBulkSyncMessage(data) {
      const synced = data.synced || 0
      const skipped = data.skipped || 0
      const failed = data.failed || 0
      const total = data.total || synced + skipped + failed

      if (data.status === 'FAILED') {
        return this.shortenMessage(data.message) || 'Ezemanage sync failed. Please try again.'
      }

      if (failed > 0) {
        return `Synced ${synced}/${total}. ${failed} failed, ${skipped} skipped.`
      }
      if (skipped > 0) {
        return `Synced ${synced}/${total}. ${skipped} skipped (no account number).`
      }
      return `Synced ${synced} device(s) successfully.`
    },

    extractApiError(err, fallback = 'Something went wrong. Please try again.') {
      const raw =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      return this.shortenMessage(raw) || fallback
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    async pollSyncJob(jobId) {
      const maxAttempts = 900
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const response = await api.get(`/devices/sync-ezemanage/jobs/${jobId}`)
        const job = response.data || {}
        this.syncProgressMessage =
          `Syncing… ${job.synced || 0} synced, ${job.skipped || 0} skipped, ${job.failed || 0} failed`

        if (job.status === 'COMPLETED' || job.status === 'FAILED') {
          return job
        }
        await this.sleep(2000)
      }
      throw new Error('Sync timed out. Please try again with fewer devices.')
    },

    async startBulkSync(deviceIds) {
      this.syncBtnDisabled = true
      this.syncProgressMessage = 'Starting Ezemanage sync…'
      try {
        const startResponse = await api.post('/devices/sync-ezemanage/bulk', { deviceIds })
        const jobId = startResponse.data?.jobId
        if (!jobId) {
          throw new Error('Could not start sync. Please try again.')
        }

        const job = await this.pollSyncJob(jobId)
        if (job.status === 'FAILED') {
          alertError(this.formatBulkSyncMessage(job))
        } else {
          alertSuccess(this.formatBulkSyncMessage(job))
        }
        this.selectedDeviceIds = []
        this.getDevices()
      } catch (err) {
        if (!err?.cancelled) {
          alertError(this.extractApiError(err, 'Failed to sync devices from Ezemanage.'))
        }
      } finally {
        this.syncBtnDisabled = false
        this.syncProgressMessage = ''
      }
    },

    async syncSelectedDevices() {
      if (!this.selectedDeviceIds.length) {
        alertError('Select at least one device to sync.')
        return
      }

      const result = await alertConfirm(`Sync ${this.selectedDeviceIds.length} selected device(s) from Ezemanage?`)
      if (!result.value) return

      await this.startBulkSync([...this.selectedDeviceIds])
    },

    async syncUnsyncedDevices() {
      try {
        const params = {
          ...this.buildListFilterParams(),
          unsynced: true,
        }
        const idsResponse = await api.get(`${window.apiUrl}/devices/ids`, { params })
        const deviceIds = idsResponse.data?.deviceIds || []

        if (!deviceIds.length) {
          alertError('No unsynced devices found for the current filters.')
          return
        }

        const result = await alertConfirm(
          `Sync ${deviceIds.length} unsynced device(s) matching the current filters from Ezemanage?`
        )
        if (!result.value) return

        await this.startBulkSync(deviceIds)
      } catch (err) {
        if (!err?.cancelled) {
          alertError(this.extractApiError(err, 'Could not find unsynced devices.'))
        }
      }
    },
  },
}
</script>

<template>
  <Teleport to="#elePageTitle">
    {{ pagetitle }}
  </Teleport>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header devices-toolbar">
          <div class="row g-2">
            <div class="col-sm-6 col-lg-3">
              <form @submit.prevent="applyFilters()">
                <div class="input-group">
                  <input type="search" class="form-control" placeholder="Device" v-model="searchHost" />
                  <span class="input-group-text cursor-pointer" @click="applyFilters()">
                    <i class="bx bx-search"></i>
                  </span>
                </div>
              </form>
            </div>
            <div class="col-sm-6 col-lg-3">
              <v-select id="status-input" v-model="filters.status" :options="statuses" placeholder="Status">
                <template #open-indicator="{ attributes, isOpen }">
                  <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                </template>
              </v-select>
            </div>
            <div class="col-sm-6 col-lg-3">
              <v-select id="category-input" v-model="filters.category" :options="categories" label="categoryName" placeholder="Category" :reduce="(category) => category.categoryName">
                <template #open-indicator="{ attributes, isOpen }">
                  <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                </template>
              </v-select>
            </div>
            <div class="col-sm-6 col-lg-3">
              <v-select id="type-input" v-model="filters.deviceType" :options="deviceTypes" label="deviceType" placeholder="Type" :reduce="(deviceType) => deviceType.deviceType">
                <template #open-indicator="{ attributes, isOpen }">
                  <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                </template>
              </v-select>
            </div>
          </div>

          <div class="d-flex flex-wrap align-items-center justify-content-end gap-2 mt-3">
            <span v-if="showEzemanageSyncActions && selectedDeviceIds.length" class="text-muted small me-1">
              {{ selectedDeviceIds.length }} selected
            </span>
            <button
              v-if="showEzemanageSyncActions"
              class="btn btn-light d-inline-flex align-items-center gap-1"
              :disabled="syncBtnDisabled || !selectedDeviceIds.length"
              @click="syncSelectedDevices()"
            >
              <i v-if="syncBtnDisabled" class="bx bx-loader bx-spin"></i>
              <i v-else class="bx bx-sync"></i>
              Sync Selected
            </button>
            <button
              v-if="showEzemanageSyncActions"
              class="btn btn-light d-inline-flex align-items-center gap-1"
              :disabled="syncBtnDisabled"
              @click="syncUnsyncedDevices()"
            >
              <i v-if="syncBtnDisabled" class="bx bx-loader bx-spin"></i>
              <i v-else class="bx bx-cloud-download"></i>
              Sync Unsynced
            </button>
            <button v-if="authUserHasPermission('device_list_export')" class="btn btn-light d-inline-flex align-items-center gap-1" @click="exportData()" :disabled="exportBtnDisabled">
              <i v-if="exportBtnDisabled" class="bx bx-loader bx-spin"></i>
              <i v-else class="bx bx-export"></i>
              Export
            </button>
            <router-link v-if="authUserHasPermission('devices_add')" to="/devices/create/bulk" class="btn btn-secondary d-inline-flex align-items-center gap-1">
              <i class="bx bx-upload align-middle"></i>
              Add Bulk
            </router-link>
            <router-link v-if="authUserHasPermission('devices_add')" to="/devices/create" class="btn btn-primary d-inline-flex align-items-center gap-1">
              <i class="bx bx-plus align-middle"></i>
              Add New
            </router-link>
          </div>
          <div v-if="syncProgressMessage" class="small text-muted text-end mt-2">{{ syncProgressMessage }}</div>

          <div class="collapse mt-3" id="filtersCollapsible">
            <div class="card border shadow-none mb-0">
              <div class="card-header d-flex align-items-center">
                <h4 class="card-title">Filters</h4>
              </div>
              <div class="card-body">
                <form>
                  <div class="row">
                    <div class="col-md-4">
                      <div class="mb-3">
                        <label for="status-input" class="form-label">Status</label>
                        <v-select id="status-input" v-model="filters.status" :options="statuses" placeholder="Select status">
                          <template #open-indicator="{ attributes, isOpen }">
                            <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                          </template>
                        </v-select>
                      </div>
                    </div>
                    <div class="col-md-12 d-flex justify-content-end">
                      <button class="btn btn-primary d-flex align-items-center me-2" data-bs-toggle="collapse" data-bs-target="#filtersCollapsible" @click.prevent="applyFilters()" :disabled="loading">
                        <i v-if="!loading" class="bx bx-check"></i>
                        <i v-else class="bx bx-loader bx-spin"></i>
                        <span class="ms-1">Apply</span>
                      </button>
                      <button class="btn btn-light d-flex align-items-center" data-bs-toggle="collapse" data-bs-target="#filtersCollapsible" @click.prevent>
                        <i class="bx bx-x"></i>
                        <span class="ms-1">Cancel</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <!-- end row -->
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-lg-12">
              <div class="table-responsive">
                <table
                  class="table mb-0 devices-table"
                  :class="authUserIsEzemanage ? 'devices-table--ezemanage' : 'devices-table--local'"
                >
                  <thead class="table-light">
                    <tr>
                      <th v-if="showEzemanageSyncActions" style="width: 40px">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :checked="allOnPageSelected"
                          @change="toggleSelectAllOnPage()"
                        />
                      </th>
                      <th
                        v-for="(column, columnIndex) in tableColumns"
                        :key="columnIndex"
                        @click="sortByColumn(column)"
                        :class="[
                          column.sortable ? 'cursor-pointer' : '',
                          `device-col-${column.name}`,
                        ]"
                      >
                        <div class="d-flex align-items-center justify-content-between">
                          <span>{{ column.label }}</span>
                          <i v-if="sortBy === column.name" :class="'bx bx-sort-' + (sortDir === 'asc' ? 'up' : 'down') + ' font-size-20'"></i>
                          <i v-else-if="column.sortable" class="bx bx-sort font-size-20"></i>
                        </div>
                      </th>
                      <th v-if="showActionColumn()">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="loading">
                      <tr v-for="n in 6" :key="n">
                        <td v-for="m in tableColCount" :key="m" class="placeholder-glow">
                          <span class="placeholder col-12"></span>
                        </td>
                      </tr>
                    </template>
                    <template v-else-if="devices.length">
                      <tr v-for="(device, index) in devices" :key="index">
                        <td v-if="showEzemanageSyncActions">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="isDeviceSelected(device.id)"
                            @change="toggleDeviceSelection(device.id)"
                          />
                        </td>
                        <td class="device-col-host text-uppercase">
                          <router-link v-if="authUserHasPermission('graphs')" :to="{ name: 'analytics-dashboard', params: { deviceId: device.id } }" title="Analytics Dashboard">
                            {{ device.host }}
                          </router-link>
                          <span v-else>{{ device.host }}</span>
                        </td>
                        <td class="device-col-ipAddress">{{ device.ipAddress || '—' }}</td>
                        <td class="device-col-deviceType">{{ device.deviceType }}</td>
                        <td class="device-col-category">{{ device.category }}</td>
                        <td v-if="authUserIsEzemanage" class="device-col-description2 text-regular-case">{{ device.description2 || '—' }}</td>
                        <td v-if="authUserIsEzemanage" class="device-col-description3 text-regular-case">{{ device.description3 || '—' }}</td>
                        <td class="device-col-status">
                          <span v-if="device.status === 'critical'" class="badge badge-alert bg-danger text-uppercase cursor-pointer" 
                          @click="handleStatusClick(device.agentHost)" style="background-color: rgb(183 19 34) !important;">
                            <i class="mdi mdi-alert-rhombus"></i>
                            {{ device.status }}
                          </span>
                          <span v-else-if="device.status === 'trouble'" class="badge badge-alert bg-warning text-uppercase cursor-pointer" 
                          @click="handleStatusClick(device.agentHost)" style="background-color: #db8f0e !important;">
                            <i class="mdi mdi-alert-circle"></i>
                            {{ device.status }}
                          </span>
                          <span v-else-if="device.status === 'attention'" class="badge bg-primary text-uppercase cursor-pointer" 
                          @click="handleStatusClick(device.agentHost)" style="background-color: #1f6acd !important;">
                            <i class="mdi mdi-alert"></i>
                            {{ device.status }}
                          </span>
                          <span v-else-if="device.status === 'clear'" class="badge badge-alert bg-success text-uppercase"
                          style="background-color: rgb(30 145 48) !important">
                            <i class="mdi mdi-check-circle"></i>
                            {{ device.status }}
                          </span>
                          <span v-else-if="device.status === 'offline'" class="badge badge-alert bg-success text-uppercase"
                          style="background-color: rgb(89 103 125) !important">
                            <i class="mdi mdi-check-circle"></i>
                            {{ device.status }}
                          </span>
                          <span v-else-if="device.status === 'maintenance'" class="badge bg-secondary text-uppercase cursor-pointer" 
                          @click="handleStatusClick(device.agentHost)" style="background-color: #9d866d !important;">
                            <i class="mdi mdi-wrench"></i>
                            {{ device.status }}
                          </span>
                          <span v-else-if="device.status === 'Awaiting Connection' || device.status === 'awaiting_connection'" class="badge badge-alert bg-secondary text-uppercase">
                            <i class="mdi mdi-timer-sand"></i>
                            Awaiting Connection
                          </span>
                        </td>
                        <td class="device-col-availability">{{ device.availability }}</td>
                        <td v-if="showActionColumn()" class="device-col-action">
                          <div class="d-flex gap-2">
                            <router-link
                              v-if="authUserHasPermission('devices_edit')"
                              :to="'/devices/' + device.id + '/edit'"
                              class="btn btn-primary bg-gradient btn-sm"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Edit"
                            >
                              <eva-icon name="edit" data-eva-height="14" data-eva-width="14" class="fill-white align-text-top"></eva-icon>
                            </router-link>
                            <button
                              v-if="authUserHasPermission('devices_delete')"
                              type="button"
                              class="btn btn-danger bg-gradient btn-sm"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Delete"
                              @click="deleteDevice(device.id)"
                            >
                              <eva-icon name="trash-2" data-eva-height="14" data-eva-width="14" class="fill-white align-text-top"></eva-icon>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr>
                        <td :colspan="tableColCount" class="text-center">No devices found</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- end row -->
        </div>
        <div v-if="!loading && devices.length" class="card-footer">
          <pagination :baseUrl="baseUrl" :totalItems="totalItems" :perPage="perPage" :currentPage="currentPage + 1" @page-changed="updatePage" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.badge-alert {
  display: inline-block;
  min-width: 152px;
  white-space: nowrap;
  align-items: center;
  text-align: center;
}

.devices-toolbar .btn {
  white-space: nowrap;
}

.devices-table {
  table-layout: fixed;
  width: 100%;
  font-size: 0.8125rem;
}

.devices-table th,
.devices-table td {
  padding-left: 0.55rem;
  padding-right: 0.55rem;
  overflow-wrap: anywhere;
  vertical-align: middle;
}

.devices-table .device-col-ipAddress {
  white-space: nowrap;
  overflow-wrap: normal;
}

.devices-table .device-col-action {
  width: 76px;
}

.devices-table .device-col-status {
  width: 172px;
  white-space: nowrap;
}

.text-regular-case {
  text-transform: none !important;
}

.devices-table--local {
  min-width: 850px;
}

.devices-table--local .device-col-host {
  width: 18%;
}

.devices-table--local .device-col-ipAddress {
  width: 18%;
}

.devices-table--local .device-col-deviceType {
  width: 15%;
}

.devices-table--local .device-col-category {
  width: 13%;
}

.devices-table--local .device-col-availability {
  width: 12%;
}

.devices-table--ezemanage {
  min-width: 1180px;
}

.devices-table--ezemanage .device-col-host {
  width: 12%;
}

.devices-table--ezemanage .device-col-ipAddress {
  width: 13%;
}

.devices-table--ezemanage .device-col-deviceType {
  width: 10%;
}

.devices-table--ezemanage .device-col-category {
  width: 9%;
}

.devices-table--ezemanage .device-col-description2 {
  width: 11%;
}

.devices-table--ezemanage .device-col-description3 {
  width: 13%;
}

.devices-table--ezemanage .device-col-availability {
  width: 9%;
}
</style>